import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet, pdf } from '@react-pdf/renderer';
import {
  FAQ_SECTIONS,
  FAQ_TITLE,
  FAQ_SUBTITLE,
  FAQ_INTRO,
} from '../data/faqContent';
import { AnswerStatus, FaqItem, STATUS_META } from '../data/faqTypes';
import { parseBold } from '../data/faqText';
import logo from '../../../assets/images/CRCEvansLogo.png';

// Print version of the commercial FAQ. Reads the same content the page renders,
// styled to match the pricing sheet PDF so the two look like one family.

const BRAND_RED = '#EB1C37';
const TEXT_DARK = '#00273F';
const TEXT_GRAY = '#464646';
const BORDER_GRAY = '#E2E5EA';
const BG_LIGHT = '#F5F6F8';
const GREEN = '#1F9A3A';
const AMBER = '#B38600';

const STATUS_COLOR: Record<AnswerStatus, string> = {
  decided: GREEN,
  proposed: AMBER,
  open: TEXT_DARK,
};

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: TEXT_DARK,
    paddingTop: 80,
    paddingBottom: 52,
    paddingHorizontal: 42,
  },
  headerBand: {
    position: 'absolute',
    top: 24,
    left: 42,
    right: 42,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 8,
    borderBottom: `2px solid ${BRAND_RED}`,
  },
  logoImage: { width: 130, height: 20 },
  logoImageSmall: { width: 95, height: 15 },
  runningHeaderText: { fontSize: 8, color: TEXT_GRAY },
  titleRow: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'baseline' },
  titleBase: { fontSize: 15, fontFamily: 'Helvetica-Bold' },
  headerMeta: { fontSize: 8, color: TEXT_GRAY, textAlign: 'right', marginTop: 4 },
  intro: { fontSize: 8.5, color: TEXT_GRAY, lineHeight: 1.55, marginBottom: 12 },
  legendBox: {
    padding: 10,
    backgroundColor: BG_LIGHT,
    border: `1px solid ${BORDER_GRAY}`,
    marginBottom: 16,
  },
  legendHeading: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    marginBottom: 6,
  },
  legendRow: { flexDirection: 'row', marginBottom: 3, alignItems: 'flex-start' },
  legendLabel: { width: 82, fontSize: 7.5, fontFamily: 'Helvetica-Bold' },
  legendMeaning: { flex: 1, fontSize: 7.5, color: TEXT_GRAY },
  sectionHeading: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_RED,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 5,
    paddingBottom: 3,
    borderBottom: `1px solid ${BRAND_RED}`,
    marginTop: 12,
  },
  sectionIntro: { fontSize: 8, color: TEXT_GRAY, lineHeight: 1.5, marginBottom: 8 },
  item: { marginBottom: 12 },
  questionRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 4 },
  question: { flex: 1, fontSize: 9.5, fontFamily: 'Helvetica-Bold', color: TEXT_DARK },
  statusTag: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginLeft: 8,
    marginTop: 1.5,
  },
  paragraph: { fontSize: 8, color: TEXT_GRAY, lineHeight: 1.55, marginBottom: 4 },
  bulletRow: { flexDirection: 'row', marginBottom: 3, paddingLeft: 4 },
  bulletMark: { fontSize: 8, color: BRAND_RED, width: 10 },
  bulletText: { flex: 1, fontSize: 8, color: TEXT_GRAY, lineHeight: 1.5 },
  noteBox: {
    marginTop: 5,
    padding: 7,
    backgroundColor: BG_LIGHT,
    borderLeft: `2px solid ${BRAND_RED}`,
  },
  noteText: { fontSize: 7.5, color: TEXT_GRAY, lineHeight: 1.5 },
  bold: { fontFamily: 'Helvetica-Bold', color: TEXT_DARK },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 42,
    right: 42,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: `1px solid ${BORDER_GRAY}`,
    paddingTop: 6,
  },
  footerText: { fontSize: 7, color: '#969696' },
});

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Brand rule: dates are always DD-MMM-YYYY.
export const formatBrandDate = (date: Date): string =>
  `${String(date.getDate()).padStart(2, '0')}-${MONTHS[date.getMonth()]}-${date.getFullYear()}`;

// Renders **bold** lead-ins by switching font face on the marked segments.
const RichText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {parseBold(text).map((segment, i) => (
      <Text key={i} style={segment.bold ? styles.bold : undefined}>{segment.text}</Text>
    ))}
  </>
);

const FaqItemBlock: React.FC<{ item: FaqItem }> = ({ item }) => (
  <View style={styles.item} wrap={false}>
    <View style={styles.questionRow}>
      <Text style={styles.question}>{item.question}</Text>
      <Text style={[styles.statusTag, { color: STATUS_COLOR[item.status] }]}>
        {STATUS_META[item.status].label}
      </Text>
    </View>

    {item.answer.map((paragraph, i) => (
      <Text key={i} style={styles.paragraph}><RichText text={paragraph} /></Text>
    ))}

    {item.bullets?.map((bullet, i) => (
      <View key={i} style={styles.bulletRow}>
        <Text style={styles.bulletMark}>&raquo;</Text>
        <Text style={styles.bulletText}><RichText text={bullet} /></Text>
      </View>
    ))}

    {item.note ? (
      <View style={styles.noteBox}>
        <Text style={styles.noteText}><RichText text={item.note} /></Text>
      </View>
    ) : null}
  </View>
);

interface FaqPDFDocumentProps {
  generatedOn: string;
}

export const FaqPDFDocument: React.FC<FaqPDFDocumentProps> = ({ generatedOn }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View
        style={styles.headerBand}
        fixed
        render={({ pageNumber }) =>
          pageNumber === 1 ? (
            <>
              <Image src={logo} style={styles.logoImage} />
              <View>
                <View style={styles.titleRow}>
                  <Text style={[styles.titleBase, { color: BRAND_RED }]}>DATA </Text>
                  <Text style={[styles.titleBase, { color: '#969696' }]}>360 </Text>
                  <Text style={[styles.titleBase, { color: TEXT_DARK }]}>COMMERCIAL FAQ</Text>
                </View>
                <Text style={styles.headerMeta}>{generatedOn}</Text>
              </View>
            </>
          ) : (
            <>
              <Image src={logo} style={styles.logoImageSmall} />
              <Text style={styles.runningHeaderText}>{FAQ_TITLE}</Text>
            </>
          )
        }
      />

      <Text style={styles.intro}>{FAQ_SUBTITLE}</Text>
      <Text style={styles.intro}>{FAQ_INTRO}</Text>

      <View style={styles.legendBox} wrap={false}>
        <Text style={styles.legendHeading}>How to read the status on each answer</Text>
        {(Object.keys(STATUS_META) as AnswerStatus[]).map(status => (
          <View key={status} style={styles.legendRow}>
            <Text style={[styles.legendLabel, { color: STATUS_COLOR[status] }]}>
              {STATUS_META[status].label}
            </Text>
            <Text style={styles.legendMeaning}>{STATUS_META[status].meaning}</Text>
          </View>
        ))}
      </View>

      {FAQ_SECTIONS.map(section => (
        <View key={section.id}>
          <Text style={styles.sectionHeading}>{section.title}</Text>
          <Text style={styles.sectionIntro}>{section.intro}</Text>
          {section.items.map(item => (
            <FaqItemBlock key={item.id} item={item} />
          ))}
        </View>
      ))}

      <View style={styles.footer} fixed>
        <Text style={styles.footerText}>CRC Evans - Internal</Text>
        <Text style={styles.footerText}>{FAQ_TITLE}</Text>
        <Text
          style={styles.footerText}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
      </View>
    </Page>
  </Document>
);

export async function generateFaqPDFBlob(generatedOn: string): Promise<Blob> {
  return pdf(<FaqPDFDocument generatedOn={generatedOn} />).toBlob();
}

export async function downloadFaqPDF(generatedOn: string): Promise<void> {
  const blob = await generateFaqPDFBlob(generatedOn);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'DATA360-Commercial-FAQ.pdf';
  a.click();
  URL.revokeObjectURL(url);
}
